import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Crosshair } from "lucide-react";
import { toast } from "sonner";
import { useDiscord } from "@/context/DiscordContext";
import useDragFocus from "@/lib/useDragFocus";
import { api, BACKEND_URL } from "@/lib/api";
import PhotoCredit from "@/components/site/PhotoCredit";

const resolveUrl = (u) => (u && u.startsWith("/") ? `${BACKEND_URL}${u}` : u);

export default function FeaturedPhoto() {
  const [photo, setPhoto] = useState(null);
  const [editing, setEditing] = useState(false);
  const { isAdmin } = useDiscord();

  useEffect(() => {
    api.get("/media/featured").then((r) => setPhoto(r.data?.featured || null)).catch(() => {});
  }, []);

  const { dragging, handlers } = useDragFocus({
    x: photo?.focus_x ?? 50,
    y: photo?.focus_y ?? 50,
    onChange: (focus_x, focus_y) => setPhoto((p) => ({ ...p, focus_x, focus_y })),
    enabled: editing,
  });

  if (!photo) return null;

  return (
    <section className="relative bg-[#0D110C] pb-24 sm:pb-32" data-testid="featured-photo">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-8 h-8 border border-[#C0A875]/60 flex items-center justify-center bg-[#161D14]">
            <Camera size={14} className="text-[#C0A875]" />
          </span>
          <span className="label-tag text-[#C0A875]">// Photo of the week</span>
          {isAdmin && !editing && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid="featured-photo-adjust"
              className="ml-auto inline-flex items-center gap-1.5 border border-[#283524] hover:border-[#C0A875] text-[#8D9A87] hover:text-[#C0A875] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors"
            >
              <Crosshair size={12} /> Adjust framing
            </button>
          )}
        </div>
        {editing && (
          <FramingControls
            photo={photo}
            onChange={(patch) => setPhoto((p) => ({ ...p, ...patch }))}
            onDone={() => setEditing(false)}
          />
        )}

        <figure
          {...handlers}
          data-testid="featured-photo-frame"
          className={`group relative overflow-hidden border bg-[#151B14] select-none ${editing ? "border-[#C0A875]" : "border-[#283524]"}`}
        >
          <img
            src={resolveUrl(photo.url)}
            alt={photo.caption || "Photo of the week"}
            data-testid="featured-photo-image"
            draggable={false}
            style={{ objectPosition: `${photo.focus_x ?? 50}% ${photo.focus_y ?? 50}%` }}
            className={`w-full aspect-[16/9] lg:aspect-[21/9] object-cover pointer-events-none ${editing ? "" : "transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"}`}
          />
          {editing && (
            <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-[#0D110C]/75 backdrop-blur-md border border-[#C0A875]/50 px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#C0A875]" data-testid="featured-photo-drag-hint">
              {dragging ? "Framing…" : "Drag the photo to frame it"} · {photo.focus_x ?? 50}% / {photo.focus_y ?? 50}%
            </div>
          )}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0D110C] via-[#0D110C]/30 to-transparent" />
          <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_160px_rgba(0,0,0,0.75)]" />

          <figcaption className={`absolute inset-x-0 bottom-0 p-6 sm:p-10 flex flex-wrap items-end justify-between gap-6 ${editing ? "pointer-events-none opacity-60" : ""}`}>
            <div className="max-w-3xl">
              {photo.caption && (
                <p
                  data-testid="featured-photo-caption"
                  className="font-display uppercase text-[#E2E4DF] text-2xl sm:text-4xl leading-[1.05] tracking-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]"
                >
                  “{photo.caption}”
                </p>
              )}
              <p className="mt-3 font-mono-ui text-xs sm:text-sm uppercase tracking-[0.2em] text-[#8D9A87]">
                Filed by <PhotoCredit item={photo} testid="featured-photo-credit" /> · {photo.year}
              </p>
            </div>
            <Link
              to="/media"
              data-testid="featured-photo-album-link"
              className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] bg-[#0D110C]/60 backdrop-blur-md px-5 py-3 font-mono-ui text-[11px] uppercase tracking-[0.25em] hover:bg-[#C0A875] hover:text-[#0D110C] transition-colors"
            >
              View the Field Album <ArrowRight size={13} />
            </Link>
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

function FramingControls({ photo, onChange, onDone }) {
  const [initial] = useState({ focus_x: photo.focus_x ?? 50, focus_y: photo.focus_y ?? 50 });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/media/featured/focus", { focus_x: photo.focus_x ?? 50, focus_y: photo.focus_y ?? 50 });
      toast.success("Framing saved.");
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => { onChange(initial); onDone(); };

  return (
    <div className="mb-4 border border-[#C0A875]/40 bg-[#161D14]/80 backdrop-blur-md p-4 flex flex-wrap items-end gap-6" data-testid="featured-photo-framing">
      <p className="flex-1 min-w-[220px] font-mono-ui text-[11px] text-[#8D9A87] leading-relaxed">
        <span className="text-[#C0A875] uppercase tracking-[0.25em] text-[10px]">// Framing mode</span><br />
        Click and drag the photo below to move it inside the frame. Save when the subject sits right.
      </p>
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange({ focus_x: 50, focus_y: 50 })} className="border border-[#283524] text-[#8D9A87] hover:text-[#E2E4DF] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]" data-testid="featured-photo-framing-reset">Center</button>
        <button type="button" onClick={cancel} className="border border-[#283524] text-[#8D9A87] hover:text-[#E2E4DF] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]" data-testid="featured-photo-framing-cancel">Cancel</button>
        <button type="button" onClick={save} disabled={saving} className="bg-[#C0A875] hover:bg-[#a99362] disabled:opacity-50 text-[#0D110C] px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] font-semibold" data-testid="featured-photo-framing-save">{saving ? "Saving…" : "Save framing"}</button>
      </div>
    </div>
  );
}
